// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { Paper, Switch, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import noop from 'lodash/noop'
import omit from 'lodash/omit'
import React from 'react'
import { Mutation } from 'react-apollo'
import DeleteButton from '../../../common/iconButtons/DeleteButton'
import EditButton from '../../../common/iconButtons/EditButton'
import { alphaSort } from '../../../common/utils'
import type { User } from '../../auth/types'
import withUser from '../../auth/withUser'
import { i18n } from '../../index'
import {
  deleteShiftResponsibilityMutation,
  fetchChainShiftResponsibilities,
  fetchStationShiftResponsibilities,
  updateShiftResponsibilityMutation,
} from '../../shiftResponsibility/shiftResponsibilityQueries'
import css from '../incentiveProduct/incentiveProductTable.css'

const updateCacheOnDelete = (query: any, fieldName: string, id: string, stationId?: string) => (cache: any) => {
  const variables = stationId
    ? {
        stationId,
      }
    : {}
  const data = cache.readQuery({
    query,
    variables,
  })
  cache.writeQuery({
    query,
    variables,
    data: {
      [fieldName]: data[fieldName].filter((item) => item.id !== id),
    },
  })
}

const getTotalNumberOfEnabledResponsibilities = (data: Array<ShiftResponsibility>): number => {
  let total = 0
  data.forEach((item: ShiftResponsibility) => {
    if (item.isEnabled) {
      ++total
    }
  })
  return total
}

type ShiftResponsibilityRowProps = {
  data: ShiftResponsibility
  stationId?: string
  user: User
  onEdit: (...args: Array<any>) => any
  onDelete: (...args: Array<any>) => any
}

const onIsEnabledToggle = (onMutate: (...args: Array<any>) => any, data: ShiftResponsibility) => {
  onMutate({
    variables: { ...data, isEnabled: !data.isEnabled },
  })
}

const ShiftResponsibilityRow = ({ data, onEdit, onDelete, user }: ShiftResponsibilityRowProps) => {
  const permissions = user.permissions || []
  return (
    <TableRow>
      <TableCell className={css.tableCell}>{data.name}</TableCell>
      <TableCell className={css.tableCell}>
        <Mutation mutation={updateShiftResponsibilityMutation}>
          {(onMutate) => (
            <Switch
              checked={data.isEnabled}
              onChange={() => onIsEnabledToggle(onMutate, data)}
              value='isEnabled'
              color='primary'
            />
          )}
        </Mutation>
      </TableCell>
      {permissions.indexOf('manage_ownStation') !== -1 ? (
        <TableCell className={css.actionsCell}>
          <EditButton onClick={() => (data.id ? onEdit(omit(data, ['__typename'])) : noop)} />
          <Mutation mutation={deleteShiftResponsibilityMutation}>
            {(onMutate) => <DeleteButton onClick={() => (data.id ? onDelete(onMutate, data.id) : noop)} />}
          </Mutation>
        </TableCell>
      ) : null}
    </TableRow>
  )
}

type ShiftResponsibilityTableProps = {
  data: Array<ShiftResponsibility>
  stationId?: string
  onEdit: (...args: Array<any>) => any
}

@withUser
class ShiftResponsibilityTable extends React.PureComponent<ShiftResponsibilityTableProps> {
  onDelete = (onMutate: (...args: Array<any>) => any, id: string) => {
    const { stationId } = this.props
    const query = stationId ? fetchStationShiftResponsibilities : fetchChainShiftResponsibilities
    const fieldName = stationId ? 'stationShiftResponsibilities' : 'chainShiftResponsibilities'
    onMutate({
      variables: {
        id,
      },
      update: updateCacheOnDelete(query, fieldName, id, stationId),
    })
  }

  render() {
    const { data, stationId, onEdit, user } = this.props
    return (
      <Paper>
        <div className={css.component}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={css.tableHeading}>{i18n.t('admin.shiftResponsibilities.label.name')}</TableCell>
                <TableCell className={css.tableHeading} padding='checkbox'>
                  {i18n.t('admin.shiftResponsibilities.label.isEnabledTotal', {
                    total: getTotalNumberOfEnabledResponsibilities(data),
                  })}
                </TableCell>
                <TableCell className={css.tableHeading} />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length ? (
                alphaSort('name', data).map((item, i) => (
                  <ShiftResponsibilityRow
                    data={item}
                    stationId={stationId}
                    onEdit={onEdit}
                    onDelete={this.onDelete}
                    key={item.id}
                    user={user}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell className={css.emptyCell}>
                    {i18n.t('admin.shiftResponsibilities.noResponsibilities')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Paper>
    )
  }
}

export default ShiftResponsibilityTable
